import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../context/ConfirmContext';
import { User, Mail, Shield, LogOut, Info } from 'lucide-react';

export const MobileProfilePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const isShared = user?.is_shared || user?.role === 'SHARED';
  const handleLogout = async () => {
    // Stronger confirmation for shared kiosk devices to avoid accidental sign-outs
    const ok = await confirm({
      title: isShared ? 'Esci dall\'account condiviso?' : 'Disconnessione',
      description: isShared
        ? 'Stai per disconnettere il dispositivo dall\'account condiviso. Solo un amministratore potrà riaccedere. Procedere?'
        : 'Vuoi uscire dal tuo account?',
      confirmText: 'Esci',
      destructive: !!isShared,
    });
    if (ok) {
      logout();
      navigate('/login');
    }
  };

  const getRoleLabel = (role) => {
    const roles = { 'ADMIN': 'Amministratore', 'MANAGER': 'Responsabile', 'SECRETARY': 'Segretaria', 'EMPLOYEE': 'Impiegato', 'SHARED': 'Account Condiviso' };
    return roles[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = { 'ADMIN': 'bg-purple-100 text-purple-700', 'MANAGER': 'bg-blue-100 text-blue-700', 'SECRETARY': 'bg-green-100 text-green-700', 'EMPLOYEE': 'bg-slate-100 text-slate-700', 'SHARED': 'bg-amber-100 text-amber-700' };
    return colors[role] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="px-4 py-6 space-y-6" data-testid="mobile-profile-page">
      {/* Profile Card */}
      <div className="bg-white rounded-2xl p-6 text-center border border-slate-200">
        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <span className="text-2xl font-bold text-blue-600">
            {user?.first_name?.charAt(0) || user?.email?.charAt(0) || user?.username?.charAt(0)?.toUpperCase() || 'U'}
          </span>
        </div>
        <h2 className="text-lg font-bold text-slate-900">
          {user?.first_name} {user?.last_name}
        </h2>
        <p className="text-sm text-slate-500">{user?.email || (user?.username ? `@${user.username}` : '')}</p>
        <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(user?.role)}`}>
          {getRoleLabel(user?.role)}
        </span>
      </div>

      {/* Info Cards */}
      <div className="bg-white rounded-2xl divide-y divide-slate-100 border border-slate-200">
        <div className="flex items-center gap-3 p-4">
          <User className="h-5 w-5 text-slate-400" />
          <div>
            <div className="text-xs text-slate-400">{isShared ? 'Nome account' : 'Nome completo'}</div>
            <div className="text-sm font-medium">{user?.first_name} {user?.last_name}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 p-4">
          <Mail className="h-5 w-5 text-slate-400" />
          <div>
            <div className="text-xs text-slate-400">{isShared ? 'Username' : 'Email'}</div>
            <div className="text-sm font-medium">{user?.email || user?.username || '-'}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 p-4">
          <Shield className="h-5 w-5 text-slate-400" />
          <div>
            <div className="text-xs text-slate-400">Ruolo</div>
            <div className="text-sm font-medium">{getRoleLabel(user?.role)}</div>
          </div>
        </div>
      </div>

      {/* Quick Stats Placeholder */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200">
        <h3 className="font-bold text-sm mb-3 text-slate-900">Statistiche Rapide</h3>
        <div className="flex gap-4">
          <div className="flex-1 text-center">
            <div className="text-2xl font-bold text-slate-300">-</div>
            <div className="text-xs text-slate-400">Bollettini questo mese</div>
          </div>
          <div className="flex-1 text-center">
            <div className="text-2xl font-bold text-slate-300">-</div>
            <div className="text-xs text-slate-400">Ore registrate</div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
          <Info className="h-3 w-3" />
          Statistiche dettagliate in arrivo
        </div>
      </div>

      {/* Legal section */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden divide-y divide-slate-100" data-testid="mobile-legal-section">
        <div className="px-4 py-3 text-xs text-slate-400 uppercase tracking-wide">Informazioni legali</div>
        <a
          href="/privacy"
          className="flex items-center justify-between px-4 py-3.5 hover:bg-slate-50 active:bg-slate-100"
          data-testid="mobile-privacy-link"
        >
          <span className="text-sm font-medium text-slate-900">Privacy Policy</span>
          <span className="text-slate-400">›</span>
        </a>
        <a
          href="/impressum"
          className="flex items-center justify-between px-4 py-3.5 hover:bg-slate-50 active:bg-slate-100"
          data-testid="mobile-impressum-link"
        >
          <span className="text-sm font-medium text-slate-900">Impressum</span>
          <span className="text-slate-400">›</span>
        </a>
      </div>

      {/* Logout */}
      <button
        onClick={handleLogout}
        className="w-full flex items-center justify-center gap-2 p-4 bg-white rounded-2xl text-red-600 font-medium border border-slate-200 hover:bg-red-50 transition-colors"
        data-testid="logout-btn"
      >
        <LogOut className="h-5 w-5" />
        Esci dall&apos;account
      </button>

      <p className="text-center text-xs text-slate-400 pb-4">BollettinoX Mobile v1.0</p>
    </div>
  );
};

export default MobileProfilePage;
